<template>
    <div class="media-browser">
        <div class="side-menu" v-if="!type">
            <ul>
                <li :class="{active: view==='ALL'}" @click="view='ALL'"><i class="fad fa-photo-film-music fa-fw"></i>All Media</li>
                <li :class="{active: view==='PHOTO'}" @click="view='PHOTO'"><i class="fad fa-images fa-fw"></i>Photos/Images</li>
                <li :class="{active: view==='VIDEO'}" @click="view='VIDEO'"><i class="fad fa-film fa-fw"></i>Videos</li>
                <li :class="{active: view==='AUDIO'}" @click="view='AUDIO'"><i class="fad fa-list-music fa-fw"></i>Audio/Music</li>
                <li :class="{active: view==='FILE'}" @click="view='FILE'"><i class="fad fa-files fa-fw"></i>Documents</li>
            </ul>
        </div>
        <div class="view" v-if="loaded">
            <card-toolbar v-if="!uploading">
                <span class="is-size-7">{{media.length}} Items ({{view | capitalize}} Media<span v-if="folder !== 'all' && folder !== 'null'">, in Folder</span><span v-if="folder === 'null'">, Unsorted</span>)</span>
                <template v-slot:right>
                    <button @click="newFolder"><i class="fas fa-folder-medical"></i>New Folder</button>
                    <button @click="deleteFolder" v-if="folder !== 'all' && folder !== 'null'"><i class="far fa-folder-xmark"></i>Delete Folder</button>
                    <b-upload v-model="file" @input="submitFile">
                        <a class="t-tb-button"><i class="fas fa-upload"></i>Upload File</a>
                    </b-upload>
                    <button @click="transload"><i class="fas fa-globe"></i>Transload File</button>
                </template>
            </card-toolbar>
            <card-container v-else class="upload-progress p-4">
                <span class="is-size-6"><i class="fas fa-spinner fa-spin-pulse mr-2"></i><strong>{{ uploadTask }}: </strong>{{ file.name }}</span>
                <b-progress type="is-info" :value="uploadPercentage" size="is-small" class="mt-2 mb-2" />
            </card-container>
            <card-container class="p-2">
                <div class="columns pt-1 pb-1">
                    <div class="column is-5" v-if="orgProvider">
                        <b-field>
                            <b-select expanded placeholder="Content Provider" icon="person-chalkboard" v-model="provider">
                                <option v-for="p in providers" :value="p.id">
                                    {{ p.title }}
                                </option>
                            </b-select>
                        </b-field>
                    </div>
                    <div class="column">
                        <b-field>
                            <b-select expanded placeholder="Folder" icon="folder" v-model="folder">
                                <option value="all">Show All</option>
                                <option value="null">Show Unsorted</option>
                                <option class="is-divider" disabled v-if="folders.length">──────────</option>
                                <option v-for="f in folders" :value="f.id">{{ f.title }}</option>
                            </b-select>
                        </b-field>
                    </div>
                    <div class="column">
                        <b-field addons>
                            <b-input placeholder="Search..."
                                     type="search"
                                     icon="search"
                                     v-model="search"
                                     expanded
                            ></b-input>
                            <p class="control">
                                <button class="button is-primary">
                                    <b-icon icon="search" />
                                </button>
                            </p>
                        </b-field>
                    </div>
                </div>
            </card-container>
            <card-list>
                <separator-list-item />
            </card-list>
            <card-container padded>
                <div class="card media-card" v-for="m in media" :class="{'is-clickable': type}" @click="$emit('choose', m)">
                    <div class="columns is-gapless mb-1 mt-1">
                        <div class="column">
                            <div class="truncate mt-2">
                                <b-tooltip :title="m.title">
                                    <i class="fad fa-fw mr-1 ml-2" :class="{'fa-image': m.type === 'PHOTO', 'fa-film': m.type === 'VIDEO', 'fa-file': m.type === 'FILE'}"></i>
                                    {{ m.title }}
                                </b-tooltip>
                            </div>
                        </div>
                        <div class="column is-narrow">
                            <b-dropdown
                                append-to-body
                                aria-role="menu"
                                scrollable
                                max-height="200"
                                trap-focus
                            >
                                <template #trigger>
                                    <a class="navbar-item" role="button">
                                        <i class="fas fa-ellipsis-vertical "></i>
                                    </a>
                                </template>
                                <a :href="m.url" target="_blank" class="has-text-grey-dark">
                                    <b-dropdown-item>
                                        <i class="fad fa-fw fa-arrow-up-right-from-square mr-2"></i>View Raw File
                                    </b-dropdown-item>
                                </a>
                                <b-dropdown-item disabled>
                                    <i class="fad fa-fw fa-trash mr-2"></i>Delete File
                                </b-dropdown-item>
                                <hr class="dropdown-divider" aria-role="menuitem">
                                <section v-if="folders.length">
                                    <b-dropdown-item @click="moveToFolder(m, {id: null})" v-if="m.folder_id">
                                        <i class="fad fa-fw fa-folder-xmark mr-2"></i>Remove from <span class="has-text-weight-bold">'{{ folders.find(f => f.id === m.folder_id).title }}'</span>
                                    </b-dropdown-item>
                                    <b-dropdown-item v-for="f in folders" @click="moveToFolder(m, f)">
                                        <i class="fad fa-fw fa-folder-arrow-up mr-2"></i>Move to <span class="has-text-weight-bold">'{{ f.title }}'</span>
                                    </b-dropdown-item>
                                </section>
                                <b-dropdown-item v-else class="is-italic" disabled>
                                    <i class="fad fa-fw fa-folder-xmark mr-2"></i>No Folders Available
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </div>
                    <div class="card-image">
                        <figure class="image is-4by3">
                            <img :src="m.thumbnail" width="320" v-if="m.type !== 'FILE'" class="m-0 p-0">
                        </figure>
                    </div>
                    <div class="card-content pt-3 subtitle is-7 has-text-grey pl-2">
                        <div v-if="m.folder_id"><i class="fad fa-fw fa-folder mr-2"></i>{{ findFolderTitle(m.folder_id) }}</div>
                        <div><i class="fad fa-fw fa-calendar mr-2"></i>{{ m.created_at | moment('MM/DD/YYYY H:MM') }}
                        </div
                        >
                        <div v-if="m.type === 'FILE'"><i class="fad fa-fw fa-file mr-2"></i>{{ m.size | formatSize }}</div>
                        <div v-if="m.type === 'VIDEO'"><i class="fad fa-fw fa-ruler-combined mr-2"></i>{{ m.width }}x{{ m.height }}, 00:{{ (m.duration / 1000) | moment('utc', 'mm:ss') }}, {{ m.size | formatSize }}</div>
                        <div v-if="m.type === 'PHOTO'"><i class="fad fa-fw fa-ruler-combined mr-2"></i>{{ m.width }}x{{ m.height }}, {{ m.size | formatSize }}</div>
                    </div>
                </div>
                <div class="is-clearfix"></div>
            </card-container>
        </div>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardToolbar from "@/TIER/components/CardToolbar";

    import {client as http, axiosInstance, apiUrl} from "@/http_client";
    import CardList from "../../TIER/components/CardList";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem";
    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import SearchListItem from "../../TIER/components/SearchListItem";
    import SeparatorListItem from "../../TIER/components/SeparatorListItem";
    import CardFooter from "../../TIER/components/CardFooter";
    import CardContainer from "../../TIER/components/CardContainer";
    import async from "async";
    import {mapGetters} from "vuex";

    export default {
        components: {CardContainer, CardFooter, SeparatorListItem, SearchListItem, ContainerListItem, SubheaderListItem, CardList, CardToolbar, CardBody, CardHeader},
        props: ['type'],
        data: function () {
            return {
                view: 'ALL',
                files: [],
                providers: [],
                provider: null,
                orgProvider: false,
                file: {},
                allFolders: [],
                folder: 'all',
                loaded: false,
                uploading: false,
                uploadTask: 'Uploading',
                uploadPercentage: 0,
                search: ''
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            media() {
                const matchesFilters = (media) => {
                    const matchesSearch = this.search === '' || media.title.toLowerCase().includes(this.search.toLowerCase());
                    const matchesFolder = this.folder === 'all' ? true :
                        this.folder === 'null' ? media.folder_id === null :
                            media.folder_id === this.folder;
                    return matchesSearch && matchesFolder;
                };

                const mediaType = this.type ? this.type : this.view;

                return this.files.filter(media =>
                    (this.view === 'ALL' || media.type === mediaType) && matchesFilters(media) && media.content_provider_id === this.provider
                );
            },
            folders() {
                return this.allFolders.filter(f => !this.provider || f.content_provider_id === this.provider);
            }
        },

        methods: {
            loadLibrary: function (force) {
                this.$emit('loading', true);
                async.series([
                    (cb) => {
                        http.get('library', {force}).then(response => {
                            this.allFolders = response.data.folders;
                            this.files = response.data.files;
                            cb();
                        }).catch(cb);
                    },
                    (cb) => {
                        if (this.providers.length === 0) {
                            http.get('organizations/self', {force: true}).then(response => {
                                this.providers = response.data.content_providers;
                                console.log('default provider: ', this.providers[0].id);
                                console.log('current user provider: ', this.currentUser.contentProviderId);

                                this.provider = this.currentUser.contentProviderId;

                                if (this.currentUser.contentProviderId === this.providers[0].id) {
                                    this.orgProvider = true;
                                }
                                console.log('set provider: ', this.provider, this.orgProvider);
                                cb();
                            }).catch(cb);
                        } else {
                            cb();
                        }
                    }
                ], err => {
                    if (err) console.log(err);
                    this.loaded = true;
                    this.$emit('loading', false);
                })
            },
            newFolder: function () {
                let title = window.prompt('Folder Title', 'Untitled Folder');
                if (title) {
                    http.post('library/folder', {title}).then(response => {
                        this.loadLibrary(true);
                    }, err => {
                        console.log(err);
                    });
                }
            },
            deleteFolder: function () {
                if (window.confirm('Are you sure you want to delete this folder? All media in this folder will be moved to unsorted.')) {
                    http.delete('library/folder/' + this.folder).then(response => {
                        this.folder = 'null';
                        this.loadLibrary(true);
                    }, err => {
                        console.log(err);
                    });
                }
            },
            moveToFolder: function (media, folder) {
                http.put('library/' + media.id, {media_id: media.id, folder_id: folder.id}).then(response => {
                    this.loadLibrary(true);
                }, err => {
                    console.log(err);
                });
            },
            findFolderTitle: function (id) {
                let folder = this.folders.find(f => f.id === id);
                return folder ? folder.title : 'Unknown Folder!';
            },
            handleFileUpload(event) {
                this.file = event.target.files[0];
            },
            submitFile() {
                this.uploading = true;
                this.uploadTask = 'Uploading';

                let formData = new FormData();
                formData.append('file', this.file);
                if (this.folder !== 'all' && this.folder !== 'null') formData.append('folder_id', this.folder);
                formData.append('content_provider_id', this.provider);

                axiosInstance.post(apiUrl + 'library/upload',
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        },
                        onUploadProgress: function (progressEvent) {
                            this.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                            if (this.uploadPercentage >= 100) {
                                this.uploadTask = 'Processing';
                                this.uploadPercentage = null;
                            }
                        }.bind(this)
                    }
                ).then(() => {
                    this.file = {};
                    this.uploading = false;
                    this.uploadPercentage = 0;
                    this.loadLibrary(true);
                }).catch(err => {
                    this.file = {};
                    this.uploading = false;
                    this.uploadPercentage = 0;
                    console.log('axios err', err);
                });
            },
            transload: function () {
                let url = window.prompt('File URL', 'https://');
                if (url) {
                    this.uploadTask = 'Processing';
                    this.uploadPercentage = null;
                    let folder_id = (this.folder !== 'all' && this.folder !== 'null') ? this.folder : null;
                    let content_provider_id = this.provider;

                    http.post('library/transload', {url, folder_id, content_provider_id}).then(response => {
                        this.loadLibrary(true);
                    }, err => {
                        console.log(err);
                        this.file = {};
                        this.uploading = false;
                        this.uploadPercentage = 0;
                    });
                }
            },
        },
        watch: {
            provider: function () {
                this.folder = 'all';
            }
        },
        mounted() {
            this.loadLibrary();
        }
    };
</script>

<style lang="scss">
    .media-browser {
        //.menu {
        //    width: 300px;
        //    border-right: 1px solid #e7e7e7;
        //}
        display: flex;
        flex-direction: row;
        flex-grow: 1;

        .upload-progress {
            background: #f3f3f3;
            border-bottom: 1px solid #dee2e6;
        }

        .side-menu {
            min-width: 290px;
            border-right: 1px solid #e7e7e7;
            overflow-y: auto;

            background-color: #fff;
            background-image: linear-gradient(white, #eeeeee);

            ul {
                list-style: none;
                margin: 0;

                li {
                    margin: 0;
                    border-bottom: 1px solid #e0e2e8;
                    padding: 1rem 1.4rem;
                    font-size: 1rem;
                    color: #4a4a4a;
                    transition: all 200ms;
                    font-weight: bold;
                    cursor: pointer;
                    background-color: rgba(255, 255, 255, 0.5);

                    &.active {
                        background-color: #336699 !important;

                        color: white;

                        .meta, i {
                            color: white;
                        }

                        &:first-child {
                            border-top: 1px solid #336699 !important;
                        }
                    }

                    &.sub-header {
                        font-weight: bold !important;
                        cursor: default !important;
                        color: #4a4a4a !important;
                        padding: 0.5rem 1.2rem 0.3rem !important;
                        font-size: 0.8rem !important;
                        text-transform: uppercase;
                        background-color: #f3f3f5 !important;
                    }

                    &:hover {
                        background-color: #567ea7;
                        color: white;

                        .meta, i {
                            color: white;
                        }
                    }

                    &:first-child {
                        border-top: none;
                    }

                    i {
                        color: #3c6691;
                        font-size: 1.5rem;
                        vertical-align: middle;
                        margin-right: 1.2rem;
                        transition: all 200ms;
                    }

                    .meta {
                        transition: color 200ms;

                        font-weight: normal;
                        font-size: 0.8rem;
                        padding-left: 3.15rem;
                        color: #737881;
                        line-height: 1rem;
                    }
                }
            }

        }

        .view {
            flex-grow: 1;
            overflow-y: auto;
            overflow-x: hidden;

            .card-image {
                background-color: white;
            }

            .media-card {
                width: 260px;
                float: left;
                margin: 0 30px 40px 0;
                height: 310px;
                overflow: hidden;
                background-color: #f8f8f8;

                .card-content {
                    border-top: 1px solid #e7e7e7;
                }

                .truncate {
                    width: 220px; /* define the width of the container */
                    white-space: nowrap; /* prevents the text from wrapping */
                    overflow: hidden; /* hides the overflow text */
                    text-overflow: ellipsis; /* adds ellipsis to the end */
                }
            }
        }
    }

</style>
